import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import OrderDetails from "../OrderDetails";
import { HelmetProvider } from 'react-helmet-async';
import Footer from "../../../../components/Footer";
import Loader from "../../../../components/Loader/Loader";
import { postPassengers } from "../../../../services/experienceOrderService";
import { experiencesService } from "../../../../services/experiencesService";
import MetaTags from "../../../../components/MetaTags";
import { type Passenger } from "../../../../components/Checkout/Passengers/PassengersDetails";
import Breadcrumbs from "../../../../components/Checkout/Breadcrumbs/BreadCrumbs";
import Header from "../../../../components/Header";
import useExperienceData from "../../../../hooks/useExperienceData";
import { useCheckoutContext } from "../../../../context/experienceCheckoutContext";
import PassengerForm from "../../../../components/Checkout/Passengers/PassengerForm";
import { BookingQuestion } from "../../../../types/bookingQuestions";
import { QuestionAnswers, sortQuestionsByPriority } from "../BookingQuestions/BookingQuestionsUtils";
import { useTravelers } from "../../../../context/travelersContext";
import Question from "../BookingQuestions/Question";
import { FormDropdown, FormSelect } from "../BookingQuestions/FormComponents";
import { getAvailableTransferModes, isPickupOfferedByThisProductOption } from "../BookingQuestions/question-helpers";
import { getBookableItemPricingRecord, useSelectedBookableItem } from "../../Detail/components/availability-and-price-helpers";

const PassengersAndBookingQuestions = () => {
    const currentStep = 0; // this is an index representing the breadcrumb step
    const [loading, setLoading] = useState(true);
    const [isFormValid, setIsFormValid] = useState(false);
    const [passengers, setPassengers] = useState<Passenger[]>([]);
    const [searchParams] = useSearchParams();
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const navigate = useNavigate();
    const { travelers, selectedDate, selectedItemCode } = useTravelers();
    const bookableItem = useSelectedBookableItem();
    const totalTravelers = Object.values(travelers).reduce((sum, count) => sum + (count || 0), 0);
    const [isSmallScreen, setIsSmallScreen] = useState(false);


    // Get order from checkout context
    const { order, loading: orderLoading, fetchOrder } = useCheckoutContext();
    const { experience, promoter } = useExperienceData();

    const travelerPickup = experience?.product.logistics.travelerPickup;

    const [selectedArrivalMode, setSelectedArrivalMode] = useState('');
    const [selectedDepartureMode, setSelectedDepartureMode] = useState('');
    const [selectedTimeEntry, setSelectedTimeEntry] = useState('');

    const [bookingQuestions, setBookingQuestions] = useState<BookingQuestion[]>([]);
    const [bookingAnswers, setBookingAnswer] = useState<QuestionAnswers>([]);
    const [travelerAnswers, settravelersAnswers] = useState<QuestionAnswers>([]);

    const numPassengersInParams = Number(searchParams.get("num_passengers"))

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                if (experience?.id) {
                    const { bookingQuestions: allQuestions } = await experiencesService.getBookingQuestions();

                    const productQuestions = experience.product.bookingQuestions;
                    const questions = allQuestions.filter(question => productQuestions.includes(question.id))
                    const filteredQuestions = questions.filter((question) => !(
                        [
                        'DATE_OF_BIRTH',
                        'FULL_NAMES_FIRST',
                        'FULL_NAMES_LAST',
                        'PASSPORT_NATIONALITY',
                        'PASSPORT_PASSPORT_NO'
                    ].includes(question.id)))
                    setBookingQuestions(filteredQuestions);

                    // Initialize PER_BOOKING questions
                    const bookingAnswers = filteredQuestions
                        .filter((q: BookingQuestion) => q.group === "PER_BOOKING")
                        .map((q: BookingQuestion) => {

                            const selectedProductOption = experience?.product.productOptions.find(product => product.productOptionCode === selectedItemCode)
                            const pickupOffered = isPickupOfferedByThisProductOption(selectedProductOption, travelerPickup);

                            if(q.id === 'PICKUP_POINT' && !pickupOffered) {
                                return  ({
                                    question: 'PICKUP_POINT',
                                    answer: 'MEET_AT_DEPARTURE_POINT',
                                    unit: 'LOCATION_REFERENCE',
                                })
                            }


                            return ({
                                question: q.id,
                                answer: ""
                            })
                        });
                      

                    setBookingAnswer(bookingAnswers);

                    if(
                        bookingAnswers.some(answer => answer.question === 'PICKUP_POINT') && 
                        !bookingAnswers.some(answer => answer.question === 'TRANSFER_ARRIVAL_MODE')
                    ) {
                        setSelectedArrivalMode("OTHER");
                    } else {
                        const availableTransferModes = getAvailableTransferModes("TRANSFER_ARRIVAL_MODE", questions, travelerPickup);
                        if(availableTransferModes[0]) {
                            setSelectedArrivalMode(availableTransferModes[0])
                        }
                    }


                    // Initialize PER_TRAVELER questions for each traveler
                    const travelersAnswers: QuestionAnswers = [];
                    for (let i = 0; i < totalTravelers; i++) {
                        filteredQuestions
                            .filter((q: BookingQuestion) => q.group === "PER_TRAVELER")
                            .forEach((q: BookingQuestion) => {
                                travelersAnswers.push({
                                    question: q.id,
                                    answer: "",
                                    travelerNum: i + 1
                                });
                            });
                    }

                    settravelersAnswers(travelersAnswers);
                }
            } catch (err) {
                console.error("Failed to load booking questions", err);
            } finally {
                setLoadingQuestions(false);
            }
        };

        fetchQuestions();
    }, [experience?.id, totalTravelers, experience?.product.bookingQuestions, experience?.product.productOptions, selectedItemCode, travelerPickup]);


    // Initialize passengers when order data is loaded
    useEffect(() => {
        if (order) {
            if (order.passengers && order.passengers.length > 0) {
                setPassengers(
                    order.passengers.map((passenger) => {
                        return {
                            id: passenger.public_id,
                            fullName: passenger.full_name,
                            phone: passenger.phone,
                            email: passenger.email,
                            dob: passenger.date_of_birth,
                            idType: passenger.id_type,
                            idNumber: passenger.id_number,
                            nationality: passenger.nationality,
                            errors: {},
                        };
                    })
                );
            } else {

                const initialPassengers = Array(numPassengersInParams).fill({
                    fullName: "",
                    phone: "",
                    email: "",
                    nationality: "",
                    dob: undefined,
                    idType: "",
                    idNumber: "",
                    errors: {},
                });

                setPassengers(initialPassengers);
            }
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order]);

    useEffect(() => {
        const formValid = passengers.every(
            (passenger) =>
                passenger.fullName &&
                passenger.phone &&
                passenger.email &&
                passenger.nationality &&
                passenger.dob &&
                passenger.idType &&
                passenger.idNumber &&
                !passenger.errors.fullName &&
                !passenger.errors.phone &&
                !passenger.errors.email &&
                !passenger.errors.nationality &&
                !passenger.errors.dob &&
                !passenger.errors.idType &&
                !passenger.errors.idNumber
        );
        setIsFormValid(formValid);
    }, [passengers]);

    useEffect(() => {
        setIsSmallScreen(window.innerWidth <= 768);

        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isSmallScreen]);

    const handlePassengerChange = (index: number, field: string, value: string | Date) => {
        const newPassengers = [...passengers];
        newPassengers[index] = { ...newPassengers[index], [field]: value };
        setPassengers(newPassengers);
    };

    const handleBookingQuestionChange = (questionId: string, value: { question: string, answer: string, unit?: string }) => {
        setBookingAnswer(prev => {
            const newAnswers = [...prev];
            const answerIndex = newAnswers.findIndex(a =>
                a.question === questionId
            );

            if (answerIndex !== -1) {
                newAnswers[answerIndex] = {
                    ...newAnswers[answerIndex],
                    answer: value.answer,
                    unit: value.unit
                };
            } else {
                throw (new Error("Internal error state handling questions"))
            }

            return newAnswers;
        });
    };

    const handleQuestionChange = (questionId: string, value: { question: string, answer: string, unit?: string, travelerNum?: number }) => {
        settravelersAnswers(prev => {
            const newAnswers = [...prev];
            const answerIndex = newAnswers.findIndex(a =>
                a.question === questionId &&
                (value.travelerNum !== undefined ? a.travelerNum === value.travelerNum : !a.travelerNum)
            );

            if (answerIndex !== -1) {
                newAnswers[answerIndex] = {
                    ...newAnswers[answerIndex],
                    answer: value.answer,
                    unit: value.unit
                };
            } else {
                throw(new Error("Internal error state handling questions"))
            }

            return newAnswers;
        });
    };

    const handlePassengerBlur = (index: number, field: string, validateFn: (value: any) => string | undefined) => {
        const newPassengers = [...passengers];
        const passenger = newPassengers[index];
        const error = validateFn(passenger[field as keyof typeof passenger]);
        newPassengers[index] = {
            ...newPassengers[index],
            errors: { ...newPassengers[index].errors, [field]: error },
        };
        setPassengers(newPassengers);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isFormValid && order) {
            setLoading(true);
            try {
                const passengersData = passengers.map((passenger) => {
                    const passengerItem: {
                        full_name: string;
                        phone: string;
                        email: string;
                        date_of_birth: Date | undefined;
                        id_type: string;
                        id_number: string;
                        nationality: string;
                        public_id?: string;
                    } = {
                        full_name: passenger.fullName || "",
                        phone: String(passenger.phone) || "",
                        email: passenger.email || "",
                        date_of_birth: passenger.dob,
                        id_type: passenger.idType || "",
                        id_number: passenger.idNumber || "",
                        nationality: passenger.nationality || ""
                    };

                    if (passenger.id) {
                        passengerItem.public_id = passenger.id;
                    }

                    return passengerItem;
                });

                const passengersDataAsBookingQuestions = passengersData.map((passenger, passengerIndex) => {
                    return Object.entries(passenger)
                        .filter(([key]) => key !== "public_id")
                        .map(([key, value]) => {
                            return ({
                                question: key,
                                answer: value,
                                travelerNum: passengerIndex + 1,
                            })
                        })
                }).flat(1)

                const timedEntry = selectedTimeEntry ? [{ question: "SELECTED_TIME_ENTRY", answer: selectedTimeEntry }] : [];

                const bookingQuestionsAnswers = [...passengersDataAsBookingQuestions, ...travelerAnswers, ...bookingAnswers, timedEntry ];
                const pickupPointAnswer = bookingAnswers.find(answer => answer.question === 'PICKUP_POINT');
                if (pickupPointAnswer && travelerPickup?.locations) {
                    const location = travelerPickup.locations.find(loc => loc.location.ref === pickupPointAnswer.answer);
                    if (location) {
                        pickupPointAnswer.answer = `${pickupPointAnswer.answer} - ${location.location.data.address}`;
                    }
                }
                // @ts-ignore
                await postPassengers({ passengers: passengersData,  bookingQuestionsAnswers}, order.booking.public_id);
                await fetchOrder();
                navigate(`/experiences/${experience?.aliasUrl}/checkout/billing`);
            } catch (e) {
                console.log(e);
                window.alert("There was a problem with the server!");
                setLoading(false);
            }
        }
    };


    let shouldDisplayTimedEntries = false;
    let timeOptions: { value: string, label: string}[] = [];
    if(bookableItem) {
        const currentPricingRecord = getBookableItemPricingRecord(bookableItem, selectedDate, travelers);
        const operatingHours = currentPricingRecord?.timedEntries;
        if(operatingHours) {
            shouldDisplayTimedEntries = true;
            timeOptions =  (operatingHours?.map((operatingHour) => {
                const unavailableDate = operatingHour.unavailableDates?.find(unavailableDate => unavailableDate.date === selectedDate);
                return {
                    value: operatingHour.startTime,
                    label: operatingHour.startTime,
                    disabled: unavailableDate ? true : false,
                }
            }));
        }
    }


    if (loading || loadingQuestions || orderLoading || !experience || !promoter) return <Loader />;


    const currentUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + window.location.search

    const perTravelerQuestions = sortQuestionsByPriority(
        bookingQuestions?.filter(q => q.group === "PER_TRAVELER") || []
    );

    const perBookingQuestions = sortQuestionsByPriority(bookingQuestions?.filter(q => q.group === "PER_BOOKING") || []);

    return (
        <HelmetProvider>
            <MetaTags
                name={promoter?.fullname || ""}
                icon={promoter?.logoURL || ""}
                url={currentUrl}
                font={experience.font ? experience.font : null}
                primaryColor={experience.colors.primary_color ? experience.colors.primary_color : null}
                secondaryColor={experience.colors.secondary_color ? experience.colors.secondary_color : null}
            />
            <Header loading={false} promoter={promoter} stock={Infinity} error={null}></Header>
            <div className="passengers__container">
                <Breadcrumbs currentStep={currentStep} orderStatus={null} noGoBack={true}></Breadcrumbs>
                <div className="main-content">
                    <div className="passenger-details__container">
                        {/* Per Booking Questions Section */}
                        {perBookingQuestions.length > 0 && (
                            <div className="passenger-details__container">
                                    <FormDropdown
                                        title="Detalles de la actividad"
                                    >
                                        <div>
                                            { shouldDisplayTimedEntries && (
                                              <FormSelect
                                                label={"Horario de la actividad"}
                                                id="question_timed_entry"
                                                value={selectedTimeEntry}
                                                required={true}
                                                onChange={(e) => {
                                                    setSelectedTimeEntry(e.target.value)
                                                }}
                                                options={[
                                                    { value: "", label: "Seleccionar un horario", disabled: true },
                                                    ...timeOptions,
                                                ]}
                                            />
                                            )}
                                            {perBookingQuestions.map((question, index) => (
                                                    <Question
                                                        itemKey={`${question.id}_booking_${index}`}
                                                        question={question}
                                                        questions={bookingQuestions}
                                                        travelerPickup={travelerPickup}
                                                        selectedArrivalMode={selectedArrivalMode}
                                                        selectedDepartureMode={selectedDepartureMode}
                                                        setSelectedArrivalMode={setSelectedArrivalMode}
                                                        setSelectedDepartureMode={setSelectedDepartureMode}
                                                        value={bookingAnswers.find(a => a.question === question.id)?.answer || ''}
                                                        unit={bookingAnswers.find(a => a.question === question.id)?.unit}
                                                        onChange={(questionId, value) => handleBookingQuestionChange(questionId, value)}
                                                    />
                                            ))}
                                        </div>
                                    </FormDropdown>
                            </div>
                        )}
                        {passengers.map((passenger, index) => (
                            <PassengerForm
                                key={index}
                                index={index}
                                data={passenger}
                                onChange={handlePassengerChange}
                                onBlur={handlePassengerBlur}
                                extraQuestions={
                                    perTravelerQuestions.map((question) => (
                                            <Question
                                                itemKey={`${question.id}_traveler_${index}`}
                                                question={question}
                                                questions={bookingQuestions}
                                                travelerPickup={travelerPickup}
                                                selectedArrivalMode={selectedArrivalMode}
                                                selectedDepartureMode={selectedDepartureMode}
                                                setSelectedArrivalMode={setSelectedArrivalMode}
                                                setSelectedDepartureMode={setSelectedDepartureMode}
                                                value={travelerAnswers.find(a =>
                                                    a.question === question.id &&
                                                    a.travelerNum === index + 1
                                                )?.answer || ''}
                                                unit={travelerAnswers.find(a =>
                                                    a.question === question.id &&
                                                    a.travelerNum === index + 1
                                                )?.unit}
                                                onChange={(questionId, value) => handleQuestionChange(questionId, { ...value, travelerNum: index + 1 })}
                                            />
                                    ))
                                }
                            />
                        ))}
                    </div>
                    <OrderDetails
                        handleCheckboxChange={() => { alert("Not implemented") }}
                        handleSubmitNextStep={handleSubmit}
                        isFormValid={true}
                        nextStepLabel={"Siguiente paso"}
                    />
                </div>
                {!isSmallScreen && (
                    <div className="checkout-actions">
                        <button type="button" onClick={handleSubmit} disabled={!isFormValid}>
                            Siguiente paso
                        </button>
                    </div>
                )}
            </div>
            <Footer loading={false} promoter={promoter}></Footer>
        </HelmetProvider>
    );
};

export default PassengersAndBookingQuestions;
